<template>
    <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
    <div
        ref="productListingCardEl"
        class="card card-compact group transition flex gap-4 h-full"
        :class="[
            direction != null && direction === 'horizontal' ? 'flex-row' : 'flex-col',
            isCampaignVisible ? 'border border-neutral-focus' : ''
        ]"
        :style="isCampaignVisible ? borderStyle : undefined"
        role="button"
        tabindex="0"
        @click="onElClick($event)"
    >
        <div class="relative bg-base-200">
            <div v-if="isProductSponsored && !isProductSponsored?.hideLabel" class="absolute top-2.5 left-2.5 text-sm uppercase tracking-wide leading-tight bg-base-100 p-2 text-base-content-subtle">
                {{ $t('product.listing.card.sponsored') }}
            </div>

            <div class="relative aspect-[4/5] flex flex-wrap items-center justify-center no-forced-colors:mix-blend-multiply">
                <div
                    class="flex flex-wrap items-center justify-center h-full w-full transition ease-in-out"
                    :class="{ 'opacity-0' : loadImageDelay }"
                >
                    <!-- Multi-image products: the card image is the first slide of the gallery carousel -->
                    <MiscCarousel
                        v-if="slicedMedia && slicedMedia.length > 1"
                        :slides="slicedMedia"
                        :label="data.name"
                        :enable-autoplay="false"
                        :enable-infinite-scroll="true"
                        :enable-arrows="true"
                        :enable-dots="true"
                        :gap="0"
                        carousel-width="100%"
                        left-arrow-class="absolute top-0 bottom-0 left-0 z-[19] flex opacity-0 transition-opacity group-hover:opacity-100"
                        right-arrow-class="absolute top-0 bottom-0 right-0 z-[19] flex opacity-0 transition-opacity group-hover:opacity-100"
                        dots-container-class="carousel__navdots absolute bottom-0 left-0 right-0 flex justify-center gap-4 opacity-0 transition-opacity group-hover:opacity-100"
                        class="product-card-image-carousel m-0 w-full h-full [&_.carousel\_\_slides]:h-full [&_.carousel\_\_slide]:h-full"
                    >
                        <template #left-arrow="{ previous }">
                            <button
                                type="button"
                                class="swiper-custom-button-prev rounded-none m-auto btn bg-white/75 shadow-none btn-square border-none hover:bg-white"
                                :aria-label="$t('product.slider.prev')"
                                @click="previous()"
                            >
                                <MiscIcon name="arrow-left" />
                            </button>
                        </template>

                        <template #right-arrow="{ next }">
                            <button
                                type="button"
                                class="swiper-custom-button-next rounded-none m-auto btn bg-white/75 shadow-none btn-square border-none hover:bg-white"
                                :aria-label="$t('product.slider.next')"
                                @click="next()"
                            >
                                <MiscIcon name="arrow-right" />
                            </button>
                        </template>

                        <template #default="{ index: slideIndex }">
                            <MiscLink
                                v-if="slicedMedia[slideIndex] != null"
                                no-prefetch
                                class="flex flex-wrap flex-row items-center justify-center h-full w-full"
                                :to="formatLink(getProductRoute(data))"
                                :aria-label="data.name"
                            >
                                <MiscImage
                                    :lazy-load="lazyImage || slideIndex !== 0"
                                    :recede="recedeOnScroll"
                                    :preload="preloadAsLcp && slideIndex === 0 ? { fetchPriority: 'high' } : false"
                                    :fetchpriority="preloadAsLcp && slideIndex === 0 ? 'high' : 'auto'"
                                    :src="slicedMedia[slideIndex]!.media?.path"
                                    :alt="slicedMedia[slideIndex]!.media?.alt"
                                    width="345"
                                    height="460"
                                    fit="fit"
                                    sizes="268px sm:208px md:223px lg:294px xl:262px 2xl:345px"
                                    background="ffffff"
                                    :modifiers="{ extend: 1, trim: '1:ffffff:1' }"
                                    class="h-full w-full object-contain max-h-[70%] hover:max-h-[75%] transition-all ease-in-out"
                                />
                            </MiscLink>
                        </template>
                    </MiscCarousel>

                    <!-- Single or missing image -->
                    <MiscLink
                        v-else
                        no-prefetch
                        class="flex flex-wrap items-center justify-center h-full w-full"
                        :to="formatLink(getProductRoute(data))"
                        :aria-label="data.name"
                    >
                        <MiscImage
                            v-if="media?.[0]?.media?.path != null"
                            :lazy-load="lazyImage"
                            :recede="recedeOnScroll"
                            :preload="preloadAsLcp ? { fetchPriority: 'high' } : false"
                            :fetchpriority="preloadAsLcp ? 'high' : 'auto'"
                            :src="media?.[0]?.media?.path"
                            :alt="media?.[0]?.media?.alt"
                            width="345"
                            height="460"
                            fit="fit"
                            sizes="268px sm:208px md:223px lg:294px xl:262px 2xl:345px"
                            background="ffffff"
                            :modifiers="{ extend: 1, trim: '1:ffffff:1' }"
                            class="h-full w-full object-contain max-h-[70%] hover:max-h-[75%] transition-all ease-in-out"
                        />

                        <img
                            v-else
                            src="/images/placeholder-image.png"
                            alt="Placeholder"
                            data-not-lazy
                        >
                    </MiscLink>
                </div>
            </div>

            <WishlistProductToggleButton :product="data" />
            <ProductListingCardBadges
                :data="data"
                :campaign-active="isCampaignVisible"
                class="bottom-7 z-10"
            />
        </div>

        <LazyCampaignDiscountBar
            v-if="isCampaignVisible"
            ref="campaignElementRef"
            class="-mt-4 shrink-0"
            :discount="promotionDetails?.discount ?? 0"
            :valid-until="promotionDetails?.validUntil ?? undefined"
            :rotate="promotionDetails?.campaignRotateDiscountCountdown ?? false"
            :color="promotionDetails?.campaignColor ?? undefined"
        />

        <MiscLink
            v-if="variant === 'full'"
            no-prefetch
            class="flex flex-col justify-between gap-4 flex-1 min-h-0"
            :class="isCampaignVisible ? 'px-3 pb-3' : 'p-0'"
            :to="formatLink(getProductRoute(data))"
        >
            <div class="flex flex-col flex-wrap gap-2">
                <span
                    v-if="data.manufacturer?.name"
                    class="block truncate w-full text-sm uppercase tracking-wide leading-tight group-hover:underline text-base-content-subtle brand-page-color-active:text-brand-page-color"
                >
                    {{ data.manufacturer?.name }}
                </span>
                <div class="text-sm font-semibold leading-tight group-hover:underline">
                    {{ data.name }}
                </div>
            </div>

            <div>
                <div v-if="listingOptionGroups.length > 0" class="mb-1 flex flex-col gap-2 text-xs text-base-content-subtle lg:text-sm">
                    <div v-for="group in listingOptionGroups" :key="group.id" class="flex flex-col lg:flex-row lg:gap-1">
                        <div class="shrink-0">
                            {{ $t('product.listing.card.availableVariants') }}
                        </div>
                        <template v-if="group.options && group.options.length > 0">
                            <div v-if="group.options.length > 6">
                                {{ $t('product.listing.card.manyVariants') }}
                            </div>
                            <div v-else class="flex flex-wrap gap-1">
                                <div v-for="(option, optionIndex) in group.options" :key="option.id">
                                    {{ getTranslatedProperty(option, 'name') }}<span v-if="optionIndex + 1 < group.options.length"> / </span>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>

                <div :class="isListPrice ? 'text-sale' : 'text-neutral brand-page-color-active:text-brand-page-color'" class="font-semibold text-sm leading-tight flex flex-wrap flex-col gap-2">
                    <div class="flex flex-wrap flex-row gap-x-3 gap-y-1">
                        <span v-if="displayFrom">{{ $t('product.listing.card.from') }}</span> <span data-cy="productListingCardPrice">{{ getFormattedPrice(unitPrice) }}</span>

                        <span class="block line-through font-normal h-5 text-neutral brand-page-color-active:text-brand-page-color">
                            <template v-if="isListPrice">{{ getFormattedPrice(price?.listPrice?.price) }}</template>
                        </span>
                    </div>
                </div>
                <div
                    v-if="data.extensions?.badges?.specialPrice?.amount && !displayFrom"
                    class="price-next-week text-xxs mt-1 text-neutral-700 brand-page-color-active:text-brand-page-color"
                >
                    {{ $t('product.listing.card.badges.specialOfferPrice') }} {{ getFormattedPrice(data.extensions?.badges?.specialPrice?.amount) }}
                </div>

                <LazyCampaignAvailabilityBar
                    v-if="isCampaignVisible && campaignAvailability"
                    class="mt-4 text-xxs"
                    :remaining="campaignAvailability.remaining"
                    :max="campaignAvailability.max"
                    label-key="campaign.listing.remaining"
                />
            </div>

            <template v-for="group in configuratorGroups" :key="group.id">
                <div v-if="group.displayType === 'color'" class="flex">
                    <LazyProductListingCardColors :data="configuratorGroups" />
                </div>
            </template>
        </MiscLink>
    </div>
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import { getProductRoute, getTranslatedProperty } from "@shopware/helpers"

interface Props {
    data: Schemas['Product']
    lazyImage?: boolean
    direction?: 'horizontal' | 'vertical',
    index?: number,
    preloadAsLcp?: boolean
    // Only infinite-scroll grid listings set this — see the comment on MiscImage's
    // `recede` prop for why carousels must not.
    recedeOnScroll?: boolean
    /** Display variant: 'full' shows all product info, 'image-only' shows only the image */
    variant?: 'full' | 'image-only'
}

const props = withDefaults(defineProps<Props>(), {
    lazyImage: true,
    direction: 'vertical',
    index: 0,
    preloadAsLcp: false,
    recedeOnScroll: false,
    variant: 'full'
})

const localePath = useLocalePath()
const { formatLink } = useInternationalization(localePath)
const { getFormattedPrice } = usePrice()
const loadImageDelay = ref(props.lazyImage)

const product = computed(() => props.data)
const { displayFrom, unitPrice, isListPrice, price } = useProductPriceCustom(product)

const {
    promotionDetails,
    isVisible: isCampaignVisible,
    availability: campaignAvailability,
    elementRef: campaignElementRef
} = useCampaignDeal(product)

const campaignColor = computed(() => promotionDetails.value?.campaignColor ?? undefined)
const { borderStyle } = useCampaignTheme(campaignColor)

const media = computed(() => {
    return sortProductMedia(props.data?.media)
})

const slicedMedia = computed(() => {
    return media?.value?.slice(0, 5)
})

const isProductSponsored = computed(() => {
    return props.data?.extensions?.sponsoredProduct
})

/** Filters configurator groups flagged for display on listing cards */
const listingOptionGroups = computed(() => {
    const configurator = props.data?.extensions?.configurator
    if (!configurator) return []

    return Object.entries(configurator)
        .filter(([key, group]) =>
            key !== 'apiAlias' &&
            typeof group === 'object' &&
            group?.translated?.customFields?.custom_property_group_info_display_listing_card
        )
        .map(([, group]) => group as Schemas['PropertyGroup'])
})

/** All configurator groups for color swatches display */
const configuratorGroups = computed((): Schemas['PropertyGroup'][] => {
    const configurator = props.data?.extensions?.configurator
    if (!configurator) return []

    return Object.entries(configurator)
        .filter(([key, group]) => key !== 'apiAlias' && typeof group === 'object')
        .map(([, group]) => group as Schemas['PropertyGroup'])
})

onMounted(() => {
    setTimeout(() => {
        loadImageDelay.value = false
    }, 300)
})

const productListingCardEl = ref<HTMLDivElement | null>(null)
const trackingData: { promotion: PromotionData | null } = inject('trackingData', { promotion: null })
const { selectPromotion, computedPromotionData } = usePromotionObserver(
    productListingCardEl,
    trackingData?.promotion,
    props.index ? props.index + 1 : 0
)

function onElClick (event: MouseEvent) {
    if (computedPromotionData?.value) selectPromotion(computedPromotionData.value, event)
}
</script>
